import { Add, Delete, Edit, GridView, Search, Undo } from '@mui/icons-material';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import produce from 'immer';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import {
  SelectableTree,
  TreeNode,
} from '@work4all/components/lib/dataDisplay/tree';
import { FileHorizontalSelect } from '@work4all/components/lib/input/horizontal-select/custom/FileHorizontalSelect';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';

import { useWidgetsDataBag } from '@work4all/data';

import { useFileViewModeSetting } from '../../hooks/useFileViewModeSetting';
import { FileContext } from '../FileContext';

import {
  fileToLayoutType,
  useFileWidgetSettings,
} from './use-file-widget-settings';

export const WidgetSettingsDialog = () => {
  const fileContext = useContext(FileContext);

  const [searchTerm, setSearchTerm] = useState('');
  const [editedLayoutId, setEditedLayoutId] = useState('');
  const [expandedIds, setExpandedIds] = useState([]);

  const {
    hiddenWidgets,
    collapsedWidgets,
    persistedFileWidgetLayouts,
    filePageWidgetGroupRegister,
    filePageDeletedWidgetGroups,
    filePageWidgetGroupNameRegister,
    activeLayoutDefinition,
    setLayoutDefinitionId,
    layoutDefinitions,
    addLayoutDefinition,
    deleteLayoutDefinition,
    editLayoutDefinition,
    addWidgetGroup,
    deleteWidgetGroups,
  } = useFileWidgetSettings(fileContext.type);

  const { widgetsDefinitions: currentWidgetDef } = useWidgetsDataBag();

  const selectedIdList = useMemo(() => {
    const ids = currentWidgetDef.definition
      .flatMap((item) => item.widgets)
      .map((x) => x.id);
    const result = ids.filter((id) => hiddenWidgets.value[id] !== true);

    return result;
  }, [currentWidgetDef.definition, hiddenWidgets]);

  const { t } = useTranslation();

  const treeData = useMemo<TreeNode[]>(() => {
    const result = currentWidgetDef.definition
      .map((header) => {
        const children = header.widgets
          .filter(
            (widget) =>
              t(header.name).toLowerCase().includes(searchTerm.toLowerCase()) ||
              t(widget.title).toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map((widget) => {
            return {
              id: widget.id,
              label: t(widget.title),
            } as TreeNode;
          });
        return children.length > 0
          ? {
              id: header.id,
              label: t(header.name),
              children,
            }
          : (null as TreeNode);
      })
      .filter(Boolean);
    return result;
  }, [currentWidgetDef.definition, searchTerm, t]);

  const handleNodeChange = useCallback(
    (selectedIds: string[]) => {
      const ids = currentWidgetDef.definition
        .flatMap((item) => item.widgets)
        .map((x) => x.id);

      const deselectedIds = ids.filter((id) => !selectedIds.includes(id));
      const selected = {};
      const deselected = {};
      for (const id of selectedIds) {
        selected[id] = false;
      }
      for (const id of deselectedIds) {
        deselected[id] = true;
      }

      hiddenWidgets.set({
        ...deselected,
        ...selected,
      });
    },
    [currentWidgetDef.definition, hiddenWidgets]
  );

  useEffect(() => {
    if (searchTerm.length > 0) {
      setExpandedIds(treeData.map((item) => item.id));
    } else {
      setExpandedIds([]);
    }
  }, [searchTerm.length, treeData]);

  const handleReset = useCallback(() => {
    deleteWidgetGroups(currentWidgetDef.definition.map((x) => x.id));

    const newFilePageDeletedWidgetGroups =
      filePageDeletedWidgetGroups.value.filter((id) =>
        currentWidgetDef.definition.map((x) => x.id).includes(id)
      );
    filePageDeletedWidgetGroups.set(newFilePageDeletedWidgetGroups);

    const layouts =
      produce(persistedFileWidgetLayouts.value?.layouts, (layouts) => {
        layouts.forEach((layout) => {
          for (const size in layout) {
            layout[size] = layout[size].filter((widget) => {
              return !currentWidgetDef.definition
                .flatMap((item) => item.widgets)
                .find((w) => w.id === widget.i);
            });
          }
        });
      }) || [];

    persistedFileWidgetLayouts.set({
      layouts,
    });

    const newFilePageWidgetGroupRegister = {};
    Object.keys(filePageWidgetGroupRegister.value).forEach((key) => {
      if (
        !currentWidgetDef.definition
          .flatMap((x) => x.widgets)
          .map((x) => x.id)
          .includes(key)
      )
        newFilePageWidgetGroupRegister[key] =
          filePageWidgetGroupRegister.value[key];
    });
    filePageWidgetGroupRegister.set(newFilePageWidgetGroupRegister);

    const newFilePageWidgetGroupNameRegister = {};
    Object.keys(filePageWidgetGroupNameRegister.value).forEach((key) => {
      if (!currentWidgetDef.definition.map((x) => x.id).includes(key))
        newFilePageWidgetGroupNameRegister[key] =
          filePageWidgetGroupNameRegister.value[key];
    });
    filePageWidgetGroupNameRegister.set(newFilePageWidgetGroupNameRegister);

    const newWidgets = {
      ...collapsedWidgets.value,
    };
    currentWidgetDef.definition
      .flatMap((item) => item.widgets)
      .forEach((w) => (newWidgets[w.id] = false));

    collapsedWidgets.set(newWidgets);
    hiddenWidgets.set(newWidgets);
  }, [
    deleteWidgetGroups,
    currentWidgetDef.definition,
    filePageDeletedWidgetGroups,
    persistedFileWidgetLayouts,
    filePageWidgetGroupRegister,
    filePageWidgetGroupNameRegister,
    collapsedWidgets,
    hiddenWidgets,
  ]);

  const handleAutoPosition = useCallback(() => {
    const layouts =
      persistedFileWidgetLayouts.value?.layouts.map((layout) => {
        return produce(layout, (draft) => {
          for (const size in draft) {
            draft[size] = draft[size].filter((widget) => {
              return !currentWidgetDef.definition
                .flatMap((item) => item.widgets)
                .find((w) => w.id === widget.i);
            });
          }
          return draft;
        });
      }) || [];

    persistedFileWidgetLayouts.set({
      layouts,
    });
  }, [currentWidgetDef.definition, persistedFileWidgetLayouts]);

  const handleAddWidgetGroup = useCallback(() => {
    addWidgetGroup(t('MASK.GROUP_MANAGEMENT.NEW'));
  }, [addWidgetGroup, t]);

  const { viewMode, setViewMode } = useFileViewModeSetting();

  return (
    <>
      <Box p="1rem">
        <FileHorizontalSelect viewMode={viewMode} setViewMode={setViewMode} />
      </Box>
      {viewMode === 'individual' && (
        <>
          <Stack p="0.5rem">
            <LabeledInput
              autoFocus
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              label={t('FILEPAGE.LAYOUT.WIDGET')}
              endAdornment={<Search />}
              style={{ background: 'transparent', border: 0 }}
            />
            <Divider style={{ padding: 0 }} />
          </Stack>
          <SelectableTree
            data={treeData}
            selected={selectedIdList}
            multiple
            selectable="leaf"
            onChange={handleNodeChange}
            expanded={expandedIds}
            onNodeToggle={(_, ids) => setExpandedIds(ids)}
          />
          <Stack p="0.5rem">
            <Divider style={{ padding: 0 }} />
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={handleAddWidgetGroup}>
                  <ListItemIcon>
                    <Add color="action" />
                  </ListItemIcon>
                  <ListItemText>{t('MASK.GROUP_MANAGEMENT.NEW')}</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={handleAutoPosition}>
                  <ListItemIcon>
                    <GridView color="action" />
                  </ListItemIcon>
                  <ListItemText>{t('COMMON.AUTOSIZE_WIDGETS')}</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={handleReset}>
                  <ListItemIcon>
                    <Undo color="action" />
                  </ListItemIcon>
                  <ListItemText>{t('COMMON.RESET_WIDGETS')}</ListItemText>
                </ListItemButton>
              </ListItem>
            </List>

            <Divider title="Layouts" style={{ padding: 0 }} />
            <List>
              {layoutDefinitions.map((item) => (
                <ListItem
                  disablePadding
                  secondaryAction={
                    item.id &&
                    editedLayoutId !== item.id && (
                      <>
                        <IconButton onClick={() => setEditedLayoutId(item.id)}>
                          <Edit color="action" />
                        </IconButton>
                        <IconButton
                          onClick={() => deleteLayoutDefinition(item.id)}
                        >
                          <Delete color="action" />
                        </IconButton>
                      </>
                    )
                  }
                >
                  {item.id && editedLayoutId === item.id ? (
                    <ListItemButton>
                      <ListItemText>
                        <input
                          defaultValue={item.name}
                          onBlur={(e) => {
                            setEditedLayoutId('');
                            editLayoutDefinition({
                              id: item.id,
                              name: e.target.value,
                            });
                          }}
                        />
                      </ListItemText>
                    </ListItemButton>
                  ) : (
                    <ListItemButton
                      selected={activeLayoutDefinition?.id === item.id}
                      onClick={() =>
                        setLayoutDefinitionId({
                          [fileToLayoutType[fileContext.type]]: item.id,
                        })
                      }
                    >
                      <ListItemText>{item.name}</ListItemText>
                    </ListItemButton>
                  )}
                </ListItem>
              ))}
              <ListItem disablePadding>
                <ListItemButton onClick={addLayoutDefinition}>
                  <ListItemIcon>
                    <Add color="action" />
                  </ListItemIcon>
                  <ListItemText>{t('COMMON.ADD_LAYOUT')}</ListItemText>
                </ListItemButton>
              </ListItem>
            </List>
          </Stack>
        </>
      )}
    </>
  );
};
